import React, { createContext, useState, useContext, useEffect } from "react";
import apiService from "../services/api";

const CompanyContext = createContext(null);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
};

// Default company definitions (fallback if API fails)
export const DEFAULT_COMPANIES = {
  KORAL: {
    id: 1,
    name: "Koral",
    displayName: "קורל",
    logo: "/images/koral.png",
    backgroundColor: "#0f172a",
  },
  GOLDCLEAN: {
    id: 2,
    name: "Gold Clean",
    displayName: "גולד קלין",
    logo: "/images/goldclean.png",
    backgroundColor: "#f8fafc",
  },
};

export const CompanyProvider = ({ children }) => {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize from localStorage or default
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const saved = localStorage.getItem("selected_company");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error("Failed to parse saved company:", error);
        return null;
      }
    }
    return null;
  });

  // Load partnerships from API on mount
  useEffect(() => {
    const loadPartnerships = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          // Not logged in, use defaults
          setPartnerships(Object.values(DEFAULT_COMPANIES));
          if (!selectedCompany) {
            setSelectedCompany(DEFAULT_COMPANIES.KORAL);
          }
          setLoading(false);
          return;
        }

        const response = await apiService.getPartnerships();
        if (response.success && response.partnerships.length > 0) {
          // Map partnerships to company format
          const mappedPartnerships = response.partnerships.map((p) => ({
            id: p.id,
            name: p.name,
            displayName: p.name,
            logo: `/images/${p.name.toLowerCase().replace(/\s+/g, "")}.png`,
            backgroundColor: "#0f172a", // Default color
            notes: p.notes,
          }));

          setPartnerships(mappedPartnerships);

          // Set selected company if not already set or if saved one doesn't exist
          if (
            !selectedCompany ||
            !mappedPartnerships.find((p) => p.id === selectedCompany.id)
          ) {
            // Try to use user's partnership from localStorage
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const userPartnership = mappedPartnerships.find(
              (p) => p.id === user.partnership_id,
            );
            setSelectedCompany(userPartnership || mappedPartnerships[0]);
          }
        } else {
          // Fallback to defaults
          setPartnerships(Object.values(DEFAULT_COMPANIES));
          if (!selectedCompany) {
            setSelectedCompany(DEFAULT_COMPANIES.KORAL);
          }
        }
      } catch (error) {
        console.error("Failed to load partnerships:", error);
        // Fallback to defaults
        setPartnerships(Object.values(DEFAULT_COMPANIES));
        if (!selectedCompany) {
          setSelectedCompany(DEFAULT_COMPANIES.KORAL);
        }
      } finally {
        setLoading(false);
      }
    };

    loadPartnerships();
  }, []);

  // Save to localStorage whenever company changes
  useEffect(() => {
    if (selectedCompany) {
      localStorage.setItem("selected_company", JSON.stringify(selectedCompany));
    }
  }, [selectedCompany]);

  const switchCompany = (partnershipId) => {
    const partnership = partnerships.find((p) => p.id === partnershipId);
    if (partnership) {
      setSelectedCompany(partnership);
      // Reload page to refresh all data with new partnership
      window.location.reload();
    }
  };

  const value = {
    selectedCompany: selectedCompany || DEFAULT_COMPANIES.KORAL,
    switchCompany,
    companies: partnerships,
    partnerships,
    loading,
    isKoral: selectedCompany?.name === "Koral",
    isGoldClean: selectedCompany?.name === "Gold Clean",
  };

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};
